// components/Integrations/ShopifyOAuthCallback.jsx
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

// Usar la misma URL base que el hook
const API_BASE_URL = process.env.REACT_APP_SHOPIFY_API_BASE_URL || 'https://conn-dev.zenitailab.com';

const ShopifyOAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Procesando autorización de Shopify...');

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Obtener parámetros de la URL
        const code = searchParams.get('code');
        const shop = searchParams.get('shop');
        const state = searchParams.get('state');
        const error = searchParams.get('error');

        console.log('🔗 Shopify callback params:', { code, shop, state, error });
        console.log('🔄 Iniciando proceso de callback...');
        console.log('🌐 URL actual:', window.location.href);
        console.log('📍 Origin:', window.location.origin);

        if (error) {
          console.error('❌ Error en OAuth Shopify:', error);
          setStatus('error');
          setMessage(`Error de autorización: ${error}`);
          
          // Limpiar datos temporales
          sessionStorage.removeItem('shopify_auth_token');
          sessionStorage.removeItem('shopify_auth_user_id');
          sessionStorage.removeItem('shopify_auth_account_id');
          
          // Notificar error a la ventana padre
          if (window.opener) {
            window.opener.postMessage({
              type: 'SHOPIFY_ERROR',
              error: error,
              error_description: searchParams.get('error_description') || 'Error de autorización'
            }, window.location.origin);
          }
          
          setTimeout(() => window.close(), 3000);
          return;
        }

        if (!code || !shop) {
          setStatus('error');
          setMessage('Parámetros de autorización faltantes');
          
          // Limpiar datos temporales
          sessionStorage.removeItem('shopify_auth_token');
          sessionStorage.removeItem('shopify_auth_user_id');
          sessionStorage.removeItem('shopify_auth_account_id');
          
          // Notificar error a la ventana padre
          if (window.opener) {
            window.opener.postMessage({
              type: 'SHOPIFY_ERROR',
              error: 'missing_parameters',
              error_description: 'Código o tienda faltante'
            }, window.location.origin);
          }
          
          setTimeout(() => window.close(), 3000);
          return;
        }

        // Enviar código al backend para intercambiar por token
        setMessage('Intercambiando código por token de acceso...');
        
        // Obtener token del sessionStorage (guardado por la ventana padre)
        let token = sessionStorage.getItem('shopify_auth_token');
        let userId = sessionStorage.getItem('shopify_auth_user_id');
        let accountId = sessionStorage.getItem('shopify_auth_account_id');
        
        console.log('🔑 Token desde sessionStorage:', token ? 'Sí' : 'No');
        console.log('🔑 User ID desde sessionStorage:', userId);
        console.log('🔑 Account ID desde sessionStorage:', accountId);
        
        // Fallback: intentar obtener del localStorage
        if (!token) {
          token = localStorage.getItem('access_token') || localStorage.getItem('authToken');
          console.log('🔑 Token obtenido del localStorage (fallback):', token ? 'Sí' : 'No');
        }
        
        if (!token) {
          throw new Error('No hay token de autenticación disponible en sessionStorage ni localStorage');
        }

        console.log('🔄 Enviando código al backend para intercambio...');
        console.log('📤 Query params:', { code, state, shop });
        console.log('📤 Headers:', {
          'Authorization': `Bearer ${token?.substring(0, 20)}...`,
          'x-user-id': userId,
          'x-account-id': accountId
        });
        console.log('🌐 API Base URL:', API_BASE_URL);
        
        // Construir URL con query params según la especificación de la API (GET)
        const callbackUrl = new URL(`${API_BASE_URL}/shopify/auth/callback`);
        callbackUrl.searchParams.append('code', code);
        callbackUrl.searchParams.append('shop', shop);
        callbackUrl.searchParams.append('state', state);
        
        console.log('🔗 Endpoint completo:', callbackUrl.toString());
        
        const response = await fetch(callbackUrl.toString(), {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'x-user-id': userId || '',
            'x-account-id': accountId || ''
          }
        });

        const data = await response.json();
        console.log('📬 Respuesta del backend callback:');
        console.log('   - Status:', response.status);
        console.log('   - OK:', response.ok);
        console.log('   - Headers:', Object.fromEntries(response.headers.entries()));
        console.log('   - Data:', data);
        
        // Mostrar texto completo si es error
        if (!response.ok) {
          console.error('❌ Error completo del backend:', {
            status: response.status,
            statusText: response.statusText,
            data: data
          });
        }

        if (response.ok) {
          console.log('✅ Callback procesado exitosamente por el backend');
          setStatus('success');
          setMessage('¡Shopify conectado exitosamente!');
          
          // Limpiar datos temporales
          sessionStorage.removeItem('shopify_auth_token');
          sessionStorage.removeItem('shopify_auth_user_id');
          sessionStorage.removeItem('shopify_auth_account_id');
          
          // Notificar éxito a la ventana padre
          if (window.opener) {
            window.opener.postMessage({
              type: 'SHOPIFY_AUTH_SUCCESS', // Cambiado para coincidir con lo que el backend espera
              shop: shop,
              code: code,
              data: data,
              timestamp: Date.now()
            }, window.location.origin);
          }
          
          setTimeout(() => window.close(), 2000);
        } else {
          console.error('❌ Error del backend en callback:', data);
          setStatus('error');
          setMessage(`Error del servidor: ${data.message || 'Error desconocido'}`);
          
          // Limpiar datos temporales
          sessionStorage.removeItem('shopify_auth_token');
          sessionStorage.removeItem('shopify_auth_user_id');
          sessionStorage.removeItem('shopify_auth_account_id');
          
          if (window.opener) {
            window.opener.postMessage({
              type: 'SHOPIFY_ERROR',
              error: 'backend_error',
              error_description: data.message || 'Error del servidor'
            }, window.location.origin);
          }
          
          setTimeout(() => window.close(), 3000);
        }
      } catch (error) {
        console.error('❌ Error procesando callback de Shopify:', error);
        setStatus('error');
        setMessage(`Error al procesar la autorización: ${error.message}`);
        
        // Limpiar datos temporales
        sessionStorage.removeItem('shopify_auth_token');
        sessionStorage.removeItem('shopify_auth_user_id');
        sessionStorage.removeItem('shopify_auth_account_id');
        
        // Notificar error a la ventana padre
        if (window.opener) {
          window.opener.postMessage({
            type: 'SHOPIFY_ERROR',
            error: 'callback_error',
            error_description: error.message
          }, window.location.origin);
        }
        
        setTimeout(() => window.close(), 3000);
      }
    };

    handleCallback();
  }, [searchParams]);

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center', 
      height: '100vh',
      padding: '2rem',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      <div style={{
        textAlign: 'center',
        maxWidth: '400px',
        padding: '2rem',
        borderRadius: '8px',
        backgroundColor: '#f8f9fa',
        border: '1px solid #e9ecef'
      }}>
        {status === 'processing' && (
          <>
            <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
            <h2 style={{ color: '#495057', marginBottom: '1rem' }}>Procesando...</h2>
          </>
        )}
        
        {status === 'success' && (
          <>
            <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>✅</div>
            <h2 style={{ color: '#28a745', marginBottom: '1rem' }}>¡Éxito!</h2>
            <button 
              onClick={() => {
                console.log('🔗 Botón manual - Enviando mensaje...');
                const message = { 
                  type: 'SHOPIFY_SUCCESS',
                  shop: searchParams.get('shop'),
                  code: searchParams.get('code'),
                  timestamp: Date.now()
                };
                
                if (window.opener) {
                  window.opener.postMessage(message, '*');
                }
                
                setTimeout(() => window.close(), 500);
              }}
              style={{
                padding: '10px 20px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                marginTop: '1rem'
              }}
            >
              Continuar (si no se cierra automáticamente)
            </button>
          </>
        )}
        
        {status === 'error' && (
          <>
            <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>❌</div>
            <h2 style={{ color: '#dc3545', marginBottom: '1rem' }}>Error</h2>
          </>
        )}
        
        <p style={{ color: '#6c757d', lineHeight: '1.5' }}>{message}</p>
        
        {status === 'success' && (
          <p style={{ color: '#6c757d', fontSize: '0.9rem', marginTop: '1rem' }}>
            Esta ventana se cerrará automáticamente...
          </p>
        )}
      </div>
    </div>
  );
};

export default ShopifyOAuthCallback;